import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { projectDocumentsAPI, projectsAPI } from "../../../api/client";
import StageTracker from "../../../components/projects/StageTracker";
import ProjectGanttChart from "../../../components/projects/ProjectGanttChart";
import ViewModeSwitch, {
  useViewModeParam,
} from "../../../components/gantt/ViewModeSwitch";
import ProjectTimeSummaryTab from "../../../components/project/ProjectTimeSummaryTab";
import AdditionalWorkPanel from "../../../components/project/AdditionalWorkPanel";
import ProjectTasksTab from "../../../components/tasks/ProjectTasksTab";
import StlViewer from "../../../components/projects/StlViewer";
import ImageLightbox from "../../../components/common/ImageLightbox";
import useAuth from "../../../hooks/useAuth";
import { PERMISSIONS } from "../../../constants/permissions";
import "../../../components/projects/Projects.css";
import { formatProjectStatus } from "../../../utils/formatProjectStatus";

const DETAIL_VIEW_MODES = ["details", "gantt"];

export default function ProjectDetailPage() {
  const { id } = useParams();
  const { user, invoicingEnabled } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [documents, setDocuments] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [selectModalOpen, setSelectModalOpen] = useState(false);
  // A failed filtered download belongs next to the ticks that caused it, not in
  // the page-footer error the unfiltered button uses.
  const [selectionError, setSelectionError] = useState("");

  const [tab, setTab] = useState("overview");
  const [viewMode, setViewMode] = useViewModeParam(DETAIL_VIEW_MODES);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [stlViewerUrl, setStlViewerUrl] = useState(null);

  const loadProject = async () => {
    try {
      setLoading(true);
      const [projectResp, docsResp] = await Promise.all([
        projectsAPI.getById(id),
        projectDocumentsAPI.list(id),
      ]);
      setProject(projectResp.data);
      setDocuments(
        docsResp.data?.data?.documents.filter(
          (doc) => doc.taskType !== "text",
        ) || [],
      );
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to load project",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProject();
  }, [id]);

  if (loading)
    return <div className="project-detail-page">Loading project...</div>;
  if (!project)
    return (
      <div className="project-detail-page">{error || "Project not found"}</div>
    );

  const showAllStages = !!(
    user?.isAdmin ||
    user?.permissions?.includes(PERMISSIONS.PROJECT_VIEW_ALL) ||
    user?.permissions?.includes(PERMISSIONS.PROJECT_ASSIGN_USERS)
  );
  const allProjectFiles = Array.isArray(project?.imageUrl)
    ? project.imageUrl
    : project?.imageUrl
      ? [project.imageUrl]
      : [];
  const projectImages = allProjectFiles.filter(
    (url) => !url.toLowerCase().endsWith(".stl"),
  );
  const projectStlFiles = allProjectFiles.filter((url) =>
    url.toLowerCase().endsWith(".stl"),
  );

  // Stage names and people that actually appear in this project's assignment
  // history — the only meaningful choices for a filtered report.
  const reportStageNames = Array.from(
    new Set(
      (project.stageAssignments || []).map(
        (assignment) => assignment.stageName || "Unnamed Stage",
      ),
    ),
  );
  const reportUsers = Array.from(
    new Map(
      (project.stageAssignments || [])
        .filter((assignment) => assignment.assignee)
        .map((assignment) => [
          assignment.assignee.id,
          {
            id: assignment.assignee.id,
            name:
              `${assignment.assignee.firstName || ""} ${assignment.assignee.lastName || ""}`.trim() ||
              "Unnamed User",
          },
        ]),
    ).values(),
  );

  // Which people actually worked which stage — the server ANDs the two filters,
  // so a stage/person pair that never happened matches no activity at all.
  const reportAssignmentPairs = (project.stageAssignments || [])
    .filter((assignment) => assignment.assignee)
    .map((assignment) => ({
      stageName: assignment.stageName || "Unnamed Stage",
      userId: assignment.assignee.id,
    }));

  const downloadReport = async (selection) => {
    if (!project?.id) return;
    const fromSelection = !!selection;
    if (!fromSelection) setSelectModalOpen(false);
    setDownloading(true);
    setError("");
    setSelectionError("");
    try {
      const resp = await projectsAPI.downloadReport(project.id, selection);
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename =
        resp.headers?.["content-disposition"]
          ?.split("filename=")?.[1]
          ?.replace(/"/g, "") || `${project.name}-report.pdf`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSelectModalOpen(false);
    } catch (err) {
      // The request asks for a blob, so an error body arrives as a Blob too and
      // has to be read back as text before its message is available.
      let message = "Failed to download report";
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text())?.message || message;
        } catch {
          // keep the generic message
        }
      } else if (data?.message) {
        message = data.message;
      }
      if (fromSelection) setSelectionError(message);
      else setError(message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="project-detail-page">
      <Link className="back-link" to="/projects">
        ← Back to Projects
      </Link>
      <div className="project-detail-header">
        <div>
          <div className="project-detail-title">
            <h1>{project.name}</h1>
            <span className={`status-badge status-${project.status}`}>
              {formatProjectStatus(project.status)}
            </span>
          </div>
          <div className="project-detail-subtitle">
            {project.customer?.name || "Unknown customer"} ·{" "}
            {project.workflow?.name || "No workflow"}
          </div>
        </div>
        <div className="project-detail-actions">
          <button
            className="btn-secondary"
            disabled={downloading}
            onClick={() => downloadReport()}
          >
            {downloading ? "Preparing Report..." : "Download Report (PDF)"}
          </button>
          <button
            className="btn-secondary"
            disabled={downloading || !project.stageAssignments?.length}
            onClick={() => setSelectModalOpen(true)}
          >
            Download Selected
          </button>
          <ViewModeSwitch
            modes={DETAIL_VIEW_MODES}
            value={viewMode}
            onChange={setViewMode}
            labels={{ details: "Project details view" }}
            ariaLabel="Project view"
          />
        </div>
      </div>
      {viewMode === "gantt" && <ProjectGanttChart project={project} />}
      {viewMode === "details" && (
        <>
          <div className="project-actions" style={{ marginBottom: 12 }}>
            <button
              className={tab === "overview" ? "btn-primary" : "btn-secondary"}
              onClick={() => setTab("overview")}
            >
              Overview
            </button>
            <button
              className={tab === "stage" ? "btn-primary" : "btn-secondary"}
              onClick={() => setTab("stage")}
            >
              Stage History
            </button>
            <button
              className={tab === "tasks" ? "btn-primary" : "btn-secondary"}
              onClick={() => setTab("tasks")}
            >
              Tasks
            </button>
            {(user?.isAdmin ||
              user?.permissions?.includes(PERMISSIONS.TIME_LOG_VIEW_ALL) ||
              user?.permissions?.includes(
                PERMISSIONS.TIME_LOG_VIEW_PROJECT,
              )) && (
              <button
                className={tab === "time" ? "btn-primary" : "btn-secondary"}
                onClick={() => setTab("time")}
              >
                Time Summary
              </button>
            )}
          </div>
          {project.status === "COMPLETED" && (
            <AdditionalWorkPanel projectId={project.id} />
          )}
          {tab === "overview" && (
            <div className="project-detail-grid">
              <div className="project-detail-aside">
                <div className="panel project-hero">
                  {projectImages.length > 0 ? (
                    <div className="project-image-gallery">
                      {projectImages.map((imageUrl, index) => (
                        <img
                          key={`${imageUrl}-${index}`}
                          src={`${process.env.REACT_APP_API_UPLOAD_URL}${imageUrl}`}
                          alt={`${project.name}-${index + 1}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => setLightboxIndex(index)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="project-img-placeholder project-hero-placeholder">
                      No Image
                    </div>
                  )}
                  {projectStlFiles.length > 0 && (
                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {projectStlFiles.map((url, i) => (
                        <button
                          key={url}
                          className="btn-secondary"
                          onClick={() =>
                            setStlViewerUrl(
                              `${process.env.REACT_APP_API_UPLOAD_URL}${url}`,
                            )
                          }
                        >
                          View 3D - {url.split("/").pop()}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="project-description">
                    {project.description || "No description provided."}
                  </p>
                </div>

                <div className="panel project-facts">
                  <h4>Project Details</h4>
                  <dl className="detail-grid">
                    <div>
                      <dt>Customer</dt>
                      <dd>{project.customer?.name || "-"}</dd>
                    </div>
                    <div>
                      <dt>Workflow</dt>
                      <dd>
                        {project.workflow?.name || "-"}{" "}
                        {project.workflow?.id &&
                          user?.permissions?.includes(
                            PERMISSIONS.WORKFLOW_CREATE,
                          ) && (
                            <Link
                              to={`/workflow-builder/${project.workflow.id}`}
                            >
                              Open
                            </Link>
                          )}
                      </dd>
                    </div>
                    <div>
                      <dt>Duration</dt>
                      <dd>
                        {project.durationValue} {project.durationUnit}
                      </dd>
                    </div>
                    <div>
                      <dt>Created By</dt>
                      <dd>
                        {project.createdBy?.firstName}{" "}
                        {project.createdBy?.lastName}
                      </dd>
                    </div>
                    <div>
                      <dt>Created At</dt>
                      <dd>{new Date(project.createdAt).toLocaleString()}</dd>
                    </div>
                    <div>
                      <dt>Updated At</dt>
                      <dd>{new Date(project.updatedAt).toLocaleString()}</dd>
                    </div>
                    {invoicingEnabled &&
                      project.invoicingStatus &&
                      project.invoicingStatus !== "NOT_READY" && (
                        <>
                          <div>
                            <dt>Invoicing Status</dt>
                            <dd>
                              {project.invoicingStatus === "INVOICED"
                                ? "Invoiced"
                                : "Ready for Invoicing"}
                            </dd>
                          </div>
                          {project.invoicedAt && (
                            <div>
                              <dt>Invoiced On</dt>
                              <dd>
                                {new Date(
                                  project.invoicedAt,
                                ).toLocaleDateString()}
                              </dd>
                            </div>
                          )}
                          {project.invoiceNumber && (
                            <div>
                              <dt>Invoice #</dt>
                              <dd>{project.invoiceNumber}</dd>
                            </div>
                          )}
                          {project.invoiceFilePath && (
                            <div>
                              <dt>Invoice File</dt>
                              <dd>
                                <a
                                  href={projectsAPI.invoiceFileUrl(
                                    project.invoiceFilePath,
                                  )}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View File
                                </a>
                              </dd>
                            </div>
                          )}
                        </>
                      )}
                  </dl>
                  {project.customFields &&
                    Object.keys(project.customFields).length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <h5 style={{ marginBottom: 8 }}>Custom Fields</h5>
                        <dl className="detail-grid">
                          {Object.entries(project.customFields).map(
                            ([key, value]) => (
                              <div key={key}>
                                <dt>{key}</dt>
                                <dd>{value || "-"}</dd>
                              </div>
                            ),
                          )}
                        </dl>
                      </div>
                    )}
                </div>

                <div className="panel project-people">
                  <h4>Allocated Users</h4>
                  {(project.allocatedUsers || []).length === 0 && (
                    <div className="small-muted">No allocated users.</div>
                  )}
                  <div className="chip-list">
                    {(project.allocatedUsers || []).map((user) => (
                      <span key={user.id} className="detail-chip">
                        {user.firstName} {user.lastName}
                        {user.roles?.length
                          ? ` · ${user.roles.map((role) => role.name).join(", ")}`
                          : user.role?.name
                            ? ` · ${user.role.name}`
                            : ""}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="panel project-docs">
                  <h4>Project Documents</h4>
                  {(project.documents || []).length === 0 ? (
                    <div className="small-muted">No project documents.</div>
                  ) : (
                    <div className="project-documents-list">
                      {project.documents.map((doc) => (
                        <div key={doc.path} className="project-document-row">
                          <span className="project-document-name" title={doc.name}>
                            {doc.name}
                          </span>
                          <span className="project-document-actions">
                            {doc.name.toLowerCase().endsWith(".stl") ? (
                              <button
                                className="btn-secondary btn-sm"
                                onClick={() =>
                                  setStlViewerUrl(projectsAPI.projectFileUrl(doc.path))
                                }
                              >
                                View 3D
                              </button>
                            ) : (
                              <a
                                className="btn-secondary btn-sm"
                                href={projectsAPI.projectFileUrl(doc.path)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Preview
                              </a>
                            )}
                            <a
                              className="btn-secondary btn-sm"
                              href={projectsAPI.projectFileUrl(doc.path, {
                                download: true,
                              })}
                            >
                              Download
                            </a>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="panel project-docs">
                  <h4>Stage Submissions</h4>
                  {documents.length === 0 && (
                    <div className="small-muted">No stage submissions.</div>
                  )}
                  {documents.map((submission) => (
                    <div
                      key={submission.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <h4 className="small-muted">{submission.taskTitle}</h4>
                        {/* ({submission.taskType}) */}
                        {submission.taskType === "text" && (
                          <div>{submission.textValue || "-"}</div>
                        )}
                      </div>

                      {submission.taskType === "checkbox" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span style={{ fontSize: 16 }}>
                            {submission.textValue === "true" ? "✅" : "☐"}
                          </span>
                          <span className="small-muted">
                            {submission.textValue === "true"
                              ? "Confirmed"
                              : "Not confirmed"}
                          </span>
                        </div>
                      )}

                      {submission.taskType === "file" &&
                        submission.filePath &&
                        (submission.filePath.toLowerCase().endsWith(".stl") ? (
                          <button
                            className="btn-secondary"
                            onClick={() =>
                              setStlViewerUrl(previewUrl(submission.filePath))
                            }
                          >
                            View 3D
                          </button>
                        ) : (
                          <a
                            className="btn-secondary"
                            href={previewUrl(submission.filePath)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Preview
                          </a>
                        ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="panel project-tracker">
                {/* <div className="project-tracker-header">
              <h3>Workflow Progress</h3>
              <p className="small-muted">
                Track current and previous stage submissions.
              </p>
            </div> */}
                <StageTracker project={{ ...project, showAllStages }} />
              </div>
            </div>
          )}
          {tab === "stage" && (
            <div className="panel project-tracker">
              <div className="project-tracker-header">
                <h3>Stage History</h3>
                <p className="small-muted">Track previous stage submissions.</p>
              </div>
              <StageTracker project={{ ...project, showAllStages }} />
            </div>
          )}
          {tab === "tasks" && <ProjectTasksTab projectId={project.id} />}
          {tab === "time" && (
            <ProjectTimeSummaryTab
              projectId={project.id}
              projectName={project.name}
            />
          )}
        </>
      )}
      {error && (
        <div className="small-muted" style={{ color: "#b91c1c" }}>
          {error}
        </div>
      )}
      {selectModalOpen && (
        <ReportSelectionModal
          stageNames={reportStageNames}
          users={reportUsers}
          assignmentPairs={reportAssignmentPairs}
          error={selectionError}
          busy={downloading}
          onConfirm={downloadReport}
          onClose={() => {
            setSelectModalOpen(false);
            setSelectionError("");
          }}
        />
      )}
      {stlViewerUrl && (
        <StlViewer url={stlViewerUrl} onClose={() => setStlViewerUrl(null)} />
      )}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={projectImages}
          index={lightboxIndex}
          onNavigate={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

function CheckRow({ checked, onChange, label, disabled = false, hint }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 13,
        padding: "6px 8px",
        borderRadius: 6,
        background: disabled ? "#f9fafb" : checked ? "#eff6ff" : "#f9fafb",
        border: "1px solid",
        borderColor: checked && !disabled ? "#bfdbfe" : "#e5e7eb",
        opacity: disabled ? 0.55 : 1,
      }}
      title={disabled && hint ? hint : undefined}
    >
      {/* Projects.css stretches every .modal-content input to 100% width, which
          would blow the checkbox up to fill the row. */}
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        style={{
          width: 16,
          height: 16,
          flex: "0 0 auto",
          margin: 0,
          padding: 0,
        }}
      />
      <span style={{ flex: 1, fontWeight: 500 }}>{label}</span>
      {disabled && hint && (
        <span style={{ fontSize: 11, color: "#9ca3af" }}>{hint}</span>
      )}
    </label>
  );
}

// Both sections start fully selected, so the default download matches the full
// report; unticking narrows it. An empty section means "no filter on that axis"
// on the server, so deselecting everything in one is blocked here instead.
function ReportSelectionModal({
  stageNames,
  users,
  assignmentPairs = [],
  error,
  busy,
  onConfirm,
  onClose,
}) {
  const [selectedStages, setSelectedStages] = useState(new Set(stageNames));
  // People are tracked by what was explicitly unticked rather than what is
  // ticked, so unticking a stage and putting it back restores that stage's
  // people instead of silently leaving them out of the report.
  const [deselectedUsers, setDeselectedUsers] = useState(new Set());

  // The server ANDs stages and people, so someone who never worked any of the
  // ticked stages can only produce an empty report. Those rows are greyed out
  // rather than offered as a dead end.
  const availableUserIds = new Set(
    assignmentPairs
      .filter((pair) => selectedStages.has(pair.stageName))
      .map((pair) => pair.userId),
  );
  const selectedUsers = new Set(
    Array.from(availableUserIds).filter((id) => !deselectedUsers.has(id)),
  );

  const toggle = (setter, value) =>
    setter((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });

  const allStagesChecked = selectedStages.size === stageNames.length;
  const allUsersChecked =
    availableUserIds.size > 0 && selectedUsers.size === availableUserIds.size;
  // A project can have unassigned stage visits and no assignees at all; that
  // section is then simply not a filter.
  const canDownload =
    selectedStages.size > 0 &&
    (availableUserIds.size === 0 || selectedUsers.size > 0);

  const sectionHeader = (title, allChecked, onToggle) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 8,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
        {title}
      </span>
      <button type="button" className="btn-secondary btn-sm" onClick={onToggle}>
        {allChecked ? "Deselect All" : "Select All"}
      </button>
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-content"
        style={{ maxWidth: 520 }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <h3 style={{ margin: 0 }}>Download Selected</h3>
            <p className="small-muted" style={{ margin: "4px 0 0" }}>
              The report will cover only the stages and people ticked below.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: 22,
              lineHeight: 1,
              color: "#64748b",
              cursor: "pointer",
              padding: 0,
            }}
          >
            &times;
          </button>
        </div>

        <div style={{ marginTop: 12 }}>
          {sectionHeader("Stages", allStagesChecked, () =>
            setSelectedStages(
              allStagesChecked ? new Set() : new Set(stageNames),
            ),
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              maxHeight: 200,
              overflowY: "auto",
            }}
          >
            {stageNames.map((name) => (
              <CheckRow
                key={name}
                checked={selectedStages.has(name)}
                onChange={() => toggle(setSelectedStages, name)}
                label={name}
              />
            ))}
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          {sectionHeader("Users", allUsersChecked, () =>
            setDeselectedUsers(
              allUsersChecked ? new Set(availableUserIds) : new Set(),
            ),
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              maxHeight: 200,
              overflowY: "auto",
            }}
          >
            {users.length ? (
              users.map((user) => {
                const unavailable = !availableUserIds.has(user.id);
                return (
                  <CheckRow
                    key={user.id}
                    checked={selectedUsers.has(user.id)}
                    onChange={() => toggle(setDeselectedUsers, user.id)}
                    label={user.name}
                    disabled={unavailable}
                    hint="no activity in the ticked stages"
                  />
                );
              })
            ) : (
              <div className="small-muted">No assignees on this project.</div>
            )}
          </div>
        </div>

        {error && (
          <div
            style={{
              marginTop: 16,
              padding: "8px 10px",
              borderRadius: 6,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#b91c1c",
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!canDownload || busy}
            onClick={() =>
              onConfirm({
                stageNames: Array.from(selectedStages),
                userIds: Array.from(selectedUsers),
              })
            }
          >
            {busy ? "Preparing..." : "Download PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

const previewUrl = (filePath) =>
  `${process.env.REACT_APP_API_UPLOAD_URL}${filePath}?token=${encodeURIComponent(
    localStorage.getItem("accessToken") || "",
  )}`;
